import * as _ from "lodash";
import * as dayjs from "dayjs";
import * as moment from "moment";
import * as hdr from "hdr-histogram-js";

interface DataItem {
  clock: number | string;
  value: number | string;
  [key: string]: any;
}

interface GroupedResult {
  clock: number;
  value: number;
  datetime: string;
}

type GroupByInterval = (
  data: DataItem[],
  intervalInMinutes: number,
  fator?: number,
) => GroupedResult[] | DataItem[];

type GroupByIntervalTimeline = (
  data: DataItem[],
  dataInicial: string,
  dataFinal: string
) => GroupedResult[] | DataItem[];

interface TrafficDataItem {
  value: number;
  [key: string]: any;
}


const roundToIntervalEnd = (timestamp: number, intervalInMinutes: number): number => {
  const duration = moment.duration(intervalInMinutes, "minutes").asSeconds();
  return Math.floor((timestamp + duration) / duration) * duration;
};

export const groupByInterval: GroupByInterval = (
  data,
  intervalInMinutes,
  fator = 1,
) => {
  if (!Array.isArray(data) || data.length === 0) return data;
  const groupData = (array: DataItem[]): GroupedResult[] => {
    return _.chain(array)
      .groupBy((item: DataItem) =>
        roundToIntervalEnd(parseInt(item.clock as string), intervalInMinutes),
      )
      .map((values: DataItem[], key: string) => ({
        clock: parseInt(key),
        value: _.sumBy(
          values,
          (item: DataItem) =>
            parseFloat(item?.value as string) / (intervalInMinutes * fator),
        ),
        datetime: dayjs(parseInt(key) * 1000).format("YYYY-MM-DD HH:mm:ss"),
      }))
      .value();
  };
  return groupData(data);
};

export const groupByIntervalTimeline: GroupByIntervalTimeline = (
  data,
  dataInicial,
  dataFinal
) => {
  if (!Array.isArray(data) || data.length === 0) return data;
  
  const unixInicial = dayjs(dataInicial).unix();
  const unixFinal = dayjs(dataFinal).unix();
  const dataSorted = [...data].sort((a, b) => (Number(a.clock)) - (Number(b.clock)));

  const itemDelayStr = dataSorted[0]?.delay as string | undefined;
  
  if (!itemDelayStr) {
    return dataSorted;
  }

  const parsedDelay = typeof itemDelayStr === 'string' ? parseTimeString(itemDelayStr) : (typeof itemDelayStr === 'number' ? itemDelayStr : null);
  const delay = parsedDelay || estimateDelay(dataSorted);
  
  const timeline = generateTimeline(unixInicial, unixFinal, delay);
  const normalizeData = fillTimelineNearest(dataSorted, timeline, delay * 0.75);
  
  return normalizeData;
};

export function calculate_95th(data: TrafficDataItem[]): number {
  const histogram: hdr.Histogram = hdr.build();
  data.map((a: TrafficDataItem) => {
    histogram.recordValue(a.value);
  });
  return histogram.getValueAtPercentile(95);
}

export function convertBpsUnit(value: string): string {
  let valInt = parseInt(value);
  const units = ["bps", "Kbps", "Mbps", "Gbps", "Tbps"];
  let unitIndex = 0;

  while (valInt >= 1000 && unitIndex < units.length - 1) {
    valInt = valInt / 1000;
    unitIndex++;
  }

  return `${valInt.toFixed(2)} ${units[unitIndex]}`;
}

export function convertBitsToBytes(bits: number | string): string {
  let bytes = parseFloat(typeof bits === "string" ? bits : bits.toString()) / 8;
  let prefixes = ["bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  let index = 0;

  while (bytes >= 1000 && index < prefixes.length - 1) {
    bytes /= 1000;
    index++;
  }

  return `${bytes.toFixed(2)} ${prefixes[index]}`;
}

function median(arr: number[]): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

function estimateDelay(historyData: DataItem[], fallback = 60): number {
  if (!Array.isArray(historyData) || historyData.length < 2) return fallback;

  const clocks = historyData
    .map((item) => typeof item.clock === "string" ? parseInt(item.clock) : item.clock as number)
    .sort((a, b) => a - b);

  const deltas: number[] = [];
  for (let i = 1; i < clocks.length; i++) {
    deltas.push(clocks[i] - clocks[i - 1]);
  }

  const preliminaryMedian = median(deltas);
  const filtered = deltas.filter((d) => d <= preliminaryMedian * 3);

  if (filtered.length === 0) return fallback;

  const finalMedian = median(filtered);
  const rounded = Math.max(60, Math.round(finalMedian / 60) * 60);

  return rounded;
}

function generateTimeline(startDate: number, endDate: number, duration: number): number[] {
  let first = Math.round((startDate + duration) / duration) * duration;
  const timeline: number[] = [];
  for (let t = first; t < endDate; t += duration) {
    timeline.push(t);
  }
  return timeline;
}

function parseTimeString(str: string | null | undefined): number | null {
  if (!str || str === "0") return null;
  const match = /^(\d+)([smhd]?)$/.exec(str);
  if (!match) return null;
  const number = parseInt(match[1], 10);
  switch (match[2]) {
    case "m":
      return number * 60;
    case "h":
      return number * 3600;
    case "d":
      return number * 86400;
    default:
      return number;
  }
}

function fillTimelineNearest(historyData: DataItem[], timeline: number[], tolerance: number): GroupedResult[] {
  const data = [...historyData].sort((a, b) => (Number(a.clock)) - (Number(b.clock)));
  let i = 0;
  return timeline.map((slot) => {
    while (
      i + 1 < data.length &&
      Math.abs((Number(data[i + 1].clock)) - slot) <= Math.abs((Number(data[i].clock)) - slot)
    ) {
      i++;
    }
    if (i < data.length && Math.abs((Number(data[i].clock)) - slot) <= tolerance) {
      const value = typeof data[i].value === "string" ? parseFloat(data[i].value as string) : data[i].value as number;
      i++;
      return { clock: slot, value, datetime: dayjs(slot * 1000).format("YYYY-MM-DD HH:mm:ss") };
    }
    return { clock: slot, value: null as any, datetime: dayjs(slot * 1000).format("YYYY-MM-DD HH:mm:ss") };
  });
}
